// Typed templates are shared by the page and condition drawer.
const same = (a, b) => JSON.stringify(a) === JSON.stringify(b);
const object = value => value !== null && typeof value === 'object' && !Array.isArray(value);

// Both directions use the same templates from comparison options. $each maps
// selected values, while $target/$value are typed placeholders, not source code.
function expand(template, target, values, value = values[0]) {
  if (template === '$target') return target;
  if (template === '$value') return value;
  if (Array.isArray(template)) return template.map(item => expand(item, target, values, value));
  if (!object(template)) return template;
  if ('$each' in template) return values.map(item => expand(template.$each, target, values, item));
  return Object.fromEntries(Object.entries(template).map(([key, item]) => [key, expand(item, target, values, value)]));
}

// Invert expansion by matching the same template and capturing its placeholders.
// Exact shapes prevent the visual editor from silently discarding unknown logic.
function match(template, value, result) {
  if (template === '$target') {
    if (result.target && result.target !== value) return false;
    result.target = value;
    return true;
  }
  if (template === '$value') { result.values.push(value); return true; }
  if (Array.isArray(template)) return Array.isArray(value) && template.length === value.length && template.every((item, index) => match(item, value[index], result));
  if (!object(template)) return template === value;
  if ('$each' in template) return Array.isArray(value) && value.length > 0 && value.length <= 100 && value.every(item => match(template.$each, item, result));
  return object(value) && same(Object.keys(template).sort(), Object.keys(value).sort()) &&
    Object.entries(template).every(([key, item]) => match(item, value[key], result));
}

// Collect referenced facts for a missing-data guard, including condition IDs.
function variables(node) {
  if (!node || typeof node !== 'object') return [];
  if (typeof node.var === 'string') return [node.var];
  for (const operator of ['some', 'all', 'none']) {
    if (Array.isArray(node[operator])) return variables(node[operator][0]);
  }
  return Object.values(node).flatMap(variables);
}

export class AutomationRules {
  static targetsFor(statement) { return statement.targets ?? [statement.target]; }

  static encode(mode, statements, schema, allowMissing = false) {
    if (!['and', 'or', 'none'].includes(mode) || !statements.length || statements.length > 20) throw new Error('Add between 1 and 20 conditions.');
    const rows = statements.map(statement => {
      const variables = this.targetsFor(statement);
      if (!Array.isArray(variables) || !variables.length || variables.length > 100 || new Set(variables).size !== variables.length) {
        throw new Error('Choose between 1 and 100 different characters.');
      }
      const targets = variables.map(variable => schema.targets.find(target => target.variable === variable));
      if (targets.some(target => !target?.comparisons.includes(statement.comparison) || target.field !== targets[0].field)) {
        throw new Error('Choose a supported target and comparison.');
      }
      if (!statement.values.length || statement.values.length > 100) throw new Error('Choose between 1 and 100 values for each condition.');
      if (statement.comparison === 'is' && statement.values.length !== 1) throw new Error('Choose one value for this check.');
      if (!allowMissing && targets.some(target => statement.values.some(value => !target.values.some(option => same(option, value))))) {
        throw new Error('Remove or replace missing values.');
      }
      if (targets.some(target => target.group)) {
        if (targets.length !== 1 || !['any', 'all', 'none'].includes(statement.groupMatch)) throw new Error('Choose how the characters in this group should match.');
        const target = targets[0];
        const collection = { var: `character_groups.${target.group}` };
        const values = target.field === 'conditions' ? statement.values.map(value => {
          const match = value?.var?.match(/^status_conditions\.status_condition_(\d+)\.id$/);
          if (!match) throw new Error('Choose available status conditions.');
          return Number(match[1]);
        }) : statement.values;
        const predicate = expand(schema.comparisons[statement.comparison], target.variable.split('.').at(-1), values);
        const operator = { any: 'some', all: 'all', none: 'none' }[statement.groupMatch];
        // Empty groups make this condition false, including all/none.
        return { and: [collection, { [operator]: [collection, predicate] }] };
      }
      if (statement.groupMatch) throw new Error('Character matching is only available for a group.');
      const checks = variables.map(variable => expand(schema.comparisons[statement.comparison], variable, statement.values));
      return checks.length === 1 ? checks[0] : { or: checks };
    });
    const expression = mode === 'none' ? { '!': [{ or: rows }] } : { [mode]: rows };
    // Missing facts invalidate the entire rule, even an "includes none" statement.
    // A successful rule dispatches its selected outcome; no match returns false.
    const required = [...new Set([...variables(expression), ...statements.flatMap(statement => variables(statement.values))])].sort();
    const rule = { if: [{ and: [{ '!': [{ missing: required }] }, expression] }, true, false] };
    if (JSON.stringify(rule).length > 16384) throw new Error('The automation is too large. Use fewer conditions.');
    return rule;
  }

  static decode(rule, schema) {
    const invalid = () => { throw new Error('This rule cannot be edited. Reset the conditions to replace it.'); };
    const expression = rule?.if?.[0]?.and?.[1];
    const mode = expression?.['!'] ? 'none' : expression && Object.keys(expression)[0];
    const rows = mode === 'none' ? expression?.['!']?.[0]?.or : expression?.[mode];
    if (!['and', 'or', 'none'].includes(mode) || !Array.isArray(rows)) return invalid();
    const decodeSingle = row => {
      for (const [comparison, template] of Object.entries(schema.comparisons)) {
        const result = { comparison, values: [] };
        if (match(template, row, result)) return result;
      }
    };
    const statements = rows.map(row => {
      const quantified = row?.and?.[1];
      const operator = quantified && Object.keys(quantified)[0];
      if (['some', 'all', 'none'].includes(operator)) {
        const [collection, predicate] = quantified[operator];
        const check = decodeSingle(predicate);
        const target = check && schema.targets.find(target => target.group && `${collection?.var}.${check.target}` === target.variable);
        if (!target) return invalid();
        return { target: target.variable, groupMatch: { some: 'any', all: 'all', none: 'none' }[operator], comparison: check.comparison,
          values: target.field === 'conditions' ? check.values.map(id => ({ var: `status_conditions.status_condition_${id}.id` })) : check.values };
      }
      const single = decodeSingle(row);
      if (single) return single;
      if (!object(row) || !same(Object.keys(row), ['or']) || !Array.isArray(row.or) || row.or.length < 2) return invalid();
      const checks = row.or.map(decodeSingle);
      if (checks.some(check => !check || check.comparison !== checks[0].comparison || !same(check.values, checks[0].values))) return invalid();
      return { targets: checks.map(check => check.target), comparison: checks[0].comparison, values: checks[0].values };
    });
    // Rebuild the full rule to verify its guard and boolean result as well as its conditions.
    // Deleted values may load for repair, but strict encoding blocks saving them.
    if (!same(this.encode(mode, statements, schema, true), rule)) return invalid();
    return { mode, statements };
  }
}
